import React, { useCallback, useContext, useEffect, useState } from "react";

import { PropertyMetersPanel } from "../../../components/property/PropertyMetersPanel";
import { ZeosModal } from "../../../components/Shared/ZeosModal";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IUnassignedMeterContract } from "../../../services/zeos-api/contracts/Property";

// MTR-P1-10 steward queue for UNASSIGNED meters (task-4063, spec v0.3 R5 / H3). Shown under the metering
// attestation queue in the Admin panel, which is super-admin only; the API enforces the same rule. One row
// per live utility meter that nobody has classified yet. "Open meters" shows the property's meters list
// (task-4062) in a modal; once a meter has a class (OTHER included) its row drops off on the refresh.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

// Spec H3: an item unclaimed for 14 days escalates.
export const OVERDUE_DAYS = 14;

const titleCase = (value?: string | null) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

const formatDate = (value?: string | null) => (value ? value.slice(0, 10) : "—");

export const UnassignedMeterQueue = () => {
  const api = useContext(ZeosApiContext);
  const [meters, setMeters] = useState<IUnassignedMeterContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openPropertyId, setOpenPropertyId] = useState<number | null>(null);

  const loadMeters = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.Property.meteringAttestationUnassignedMetersAsync();
      if (isSuccess(response.zeosStatus) && Array.isArray(response.data)) {
        setMeters(response.data);
      } else {
        setError(response.message || "Could not load unassigned meters.");
      }
    } catch {
      setError("Could not load unassigned meters.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadMeters();
  }, [loadMeters]);

  return (
    <div className="bg-white border border-gray-200 rounded p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-black">
          Unassigned meters{!loading && !error ? ` (${meters.length})` : ""}
        </h3>
        <button
          type="button"
          className="text-xs text-zeos-orange-dk hover:underline disabled:opacity-50"
          onClick={() => loadMeters()}
          disabled={loading}
        >
          Refresh
        </button>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Utility meters nobody has classified yet. Each one stays here until it is given a class: use Open
        meters to classify (Other with a label for anything outside the list).
      </p>
      {error && (
        <p role="alert" className="text-xs text-red-600 mb-3">
          {error}
        </p>
      )}
      {loading && <p className="text-sm text-gray-600">Loading meters…</p>}
      {!loading && meters.length === 0 && !error && (
        <p className="text-sm text-gray-600">Nothing waiting. Every utility meter is classified.</p>
      )}
      {!loading && meters.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200" aria-label="Unassigned meters">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-3 py-2 border-b">Company</th>
                <th className="px-3 py-2 border-b">Property</th>
                <th className="px-3 py-2 border-b">Service</th>
                <th className="px-3 py-2 border-b">Meter</th>
                <th className="px-3 py-2 border-b">Source</th>
                <th className="px-3 py-2 border-b">Role</th>
                <th className="px-3 py-2 border-b">Status</th>
                <th className="px-3 py-2 border-b">Last reported</th>
                <th className="px-3 py-2 border-b">Waiting</th>
              </tr>
            </thead>
            <tbody>
              {meters.map((meter) => {
                const overdue = (meter.daysWaiting ?? 0) >= OVERDUE_DAYS;
                return (
                  <tr key={meter.meterId} className="border-t align-top">
                    <td className="px-3 py-2">{meter.companyName}</td>
                    <td className="px-3 py-2">
                      <div>{meter.propertyName}</div>
                      <div className="text-xs text-gray-500">
                        {[meter.city, meter.state].filter(Boolean).join(", ")}
                      </div>
                      <button
                        type="button"
                        aria-label={`Open meters for ${meter.propertyName}`}
                        className="text-xs text-zeos-orange-dk hover:underline"
                        onClick={() => setOpenPropertyId(meter.propertyId)}
                      >
                        Open meters
                      </button>
                    </td>
                    <td className="px-3 py-2">{meter.serviceName ?? "Unknown"}</td>
                    <td className="px-3 py-2">
                      <div>{meter.meterUid || meter.meterNumber || `Meter ${meter.meterId}`}</div>
                      {meter.serviceAccountNumber && (
                        <div className="ph-mask text-xs text-gray-500">SAID {meter.serviceAccountNumber}</div>
                      )}
                      {meter.serviceAddress && <div className="text-xs text-gray-500">{meter.serviceAddress}</div>}
                    </td>
                    <td className="px-3 py-2">{meter.source}</td>
                    <td className="px-3 py-2">{titleCase(meter.rollupRole)}</td>
                    <td className="px-3 py-2">{titleCase(meter.connectionStatus)}</td>
                    <td className="px-3 py-2">{formatDate(meter.lastReportedDate)}</td>
                    <td className={`px-3 py-2 ${overdue ? "text-red-600 font-semibold" : ""}`}>
                      {meter.daysWaiting == null ? "—" : `${meter.daysWaiting} d`}
                      {overdue && <span className="sr-only"> (overdue)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ZeosModal isOpen={openPropertyId !== null} onClose={() => setOpenPropertyId(null)}>
        {openPropertyId !== null && <PropertyMetersPanel propertyId={openPropertyId} onChanged={loadMeters} />}
      </ZeosModal>
    </div>
  );
};
